(ns practitest.frontend.components.attachments
  (:require
    [ajax.core :as ajax]
    [clojure.string :as string]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.search-box :as search-box]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.uri :refer [set-query-param]]
    [practitest.frontend.components.confirmation :as confirmation]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.overflow :as overflow]
    [practitest.frontend.components.overlay2 :as overlay2]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.events :as events]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

;;; subscriptions

(defn process-backend-attachment-data [data]
  (merge data
         {:size (:file_size data)
          :created-at (:created_at data)
          :type (:file_type data)
          :src (:url data)}))

(re-frame/reg-sub
 ::attachments
 (fn [[_ entity]]
   (re-frame/subscribe [::subs/entity entity]))
 (fn [{:keys [attachments]} _]
   (mapv process-backend-attachment-data (:data attachments))))

(re-frame/reg-sub
 ::stage-attachments
 (fn [[_ entity]]
   (re-frame/subscribe [::subs/entity entity]))
 (fn [{:keys [attachments]} _]
   (:stage attachments)))

(re-frame/reg-sub
 ::notifications
 (fn [[_ entity]]
   (re-frame/subscribe [::subs/entity entity]))
 (fn [{:keys [attachments]} _]
   (:notifications attachments)))

(re-frame/reg-sub
 ::unsaved-attachments?
 (fn [[_ entity]]
   (re-frame/subscribe [::subs/entity entity]))
 (fn [{:keys [attachments]} _]
    ;; can be in-progress attachments.
    ;; staged attachments that were uploaded to the staging area (for new entity)
    ;; are saved after the create (seq (:stage attachments))
    (seq (:requests attachments))))

;; events
;; NOTE: we currently uploading each attachment separately because we want to have progress for individual attachments
;;

(re-frame/reg-event-db
 ::track-upload
 (fn [db [_ entity upload-id request]]
   (update-in db [:entity entity :attachments :requests] assoc upload-id request)))

(re-frame/reg-event-db
 ::failed-upload
 (fn [db [_ entity upload-id _file result-or-error]]
   (update-in db [:entity entity :attachments]
              (fn [attachments]
                (cond-> attachments
                  :always (update-in [:notifications :failed] (fnil inc 0))
                  upload-id (update-in [:notifications :uploading] (fnil dec 0))
                  upload-id (update :requests dissoc upload-id)
                  (nil? upload-id) (update-in [:notifications :errors] conj result-or-error))))))

(re-frame/reg-event-fx
 ::cancel-uploads
 (fn [{:keys [db]} [_ entity]]
   (doseq [req (vals (get-in db [:entity entity :attachments :requests]))]
     (.abort req))
   {:db (update-in db [:entity entity :attachments]
                   (fn [attachments]
                     (-> attachments
                         (assoc-in [:notifications :uploading] 0)
                         (assoc :requests nil))))}))

(defn- entity-type->backend
  [entity-type]
  (case entity-type
    :testset
    "Suite"

    :milestone
    "Milestone"

    :issue
    "Issue"))

;; upload
(defn upload-attachment [{db :db}
                         [_ entity-type entity-id file]]
  (let [upload-id (random-uuid)]
    {:db (update-in db [:entity entity-type :attachments :notifications :uploading]
                    (fnil inc 0))
     :http-xhrio
     {:method :post
      :uri (broutes/get-url "pattachments#multiupload"
                            {:project_id (subs/get-current-project-id db)})
      :body   (doto (js/FormData.)
                (.append "attachable_id" entity-id)
                (.append "attachable_type" (entity-type->backend entity-type))
                (.append "pattachments[]" file (.-name file)))
      :progress-handler (fn [event]
                          (js/console.log "upload-attachment:progress" event))
      :response-format (ajax/json-response-format {:keywords? true})
      :on-request [::track-upload entity-type upload-id]
      :on-success [::uploaded entity-type upload-id file]
      :on-failure [::failed-upload entity-type upload-id file]}}))

(re-frame/reg-event-fx
  ::upload
  upload-attachment)

(re-frame/reg-event-fx
  ::uploaded
  (fn [{:keys [db]} [_ entity upload-id _file result]]
    ;; NOTE: we can get here if upload fails because of validation (max size or max attachments)
    ;;       current way to distinguish between successful and unsuccessful uploads is to check if `(:result result)` is "ok" or "error"
   ;;       if it's "error", then we can dig the actual error message (what validation failed) from `(:message result)`
   ;; TODO: mixpanel probably should be an effect
   (when (= "ok" (:result result))
     ;; TODO try/catch inside mixpanel/track + macro ?
     (try
       (mixpanel/track "Attachment Uploaded" {"Entity" (name entity)})
       (catch js/Error e
         (js/console.warn "Failed to track" e))))
   {:db (update-in db [:entity entity :attachments]
                   (fn [attachments]
                     (cond-> attachments
                       :always (update-in [:notifications :uploading] (fnil dec 0))
                       :always (update :requests dissoc upload-id)
                       (= "ok" (:result result)) (update-in [:notifications :uploaded] (fnil inc 0))
                       (= "ok" (:result result)) (update :data concat (:data result))
                       (= "error" (:result result)) (update-in [:notifications :failed] (fnil inc 0))
                       (= "error" (:result result)) (update-in [:notifications :errors] conj (:message result)))))
    :fx [[:dispatch [::events/fetch-history]]]}))

;; stage-upload
(defn stage-upload-attachment [{db :db}
                               [_ entity-type file]]
  (let [uuid (str (random-uuid))]
    {:db (update-in db [:entity entity-type :attachments :notifications :uploading]
                    (fnil inc 0))
     :http-xhrio
     {:method :post
      :uri (broutes/get-url "pattachments#staging_upload" {:project_id (subs/get-current-project-id db)})
      :body (doto (js/FormData.)
              (.append "file" file (.-name file))
              (.append "uuid" uuid))
      :progress-handler (fn [e]
                          (js/console.log "stage-upload-attachment" e))
      :response-format (ajax/json-response-format {:keywords? true})
      :on-request [::track-upload entity-type uuid]
      :on-success [::stage-uploaded entity-type uuid file]
      :on-failure [::failed-upload entity-type uuid file]}}))

(re-frame/reg-event-fx
 ::stage-upload
 stage-upload-attachment)

(re-frame/reg-event-fx
 ::stage-uploaded
 (fn [{:keys [db]} [_ entity uuid file _result]]
    ;; NOTE: we do not have to re-download the attachment to display it,
    ;;       let's just read the file and use dataURL
   (let [image? (string/starts-with? (.-type file) "image/")]
     (when image?
       (let [reader (js/FileReader.)]
         (.addEventListener reader "load"
                            (fn []
                              (re-frame/dispatch [::set-stage-uploaded-data entity file uuid
                                                  (.-result reader)])))
         (.readAsDataURL reader file)))
     {:db (update-in db [:entity entity :attachments]
                     (fn [attachments]
                       (-> attachments
                           (update-in [:notifications :uploading] (fnil dec 0))
                           (update-in [:notifications :uploaded] (fnil inc 0))
                           (update :requests dissoc uuid))))
      :fx [(when-not image? [:dispatch [::set-stage-uploaded-data entity file uuid]])]})))

(re-frame/reg-event-db
 ::set-stage-uploaded-data
 (fn [db [_ entity file uuid url]]
   (update-in db [:entity entity :attachments :stage] conj
              {:id uuid
               :name (.-name file)
               :size (.-size file)
               :type (.-type file)
               :url url})))

;; delete
(defn delete-attachment [_ [_ entity {:keys [id src] :as attachment}]]
  ;; TODO: copy-pasted from `practitest.frontend.events`
  ;;       i didn't want to re-fetch all the attachments, so the `on-success` is different
  ;;       We need to add another endpoint for deletion to simplify this request
  ;;       and response (it's a fairly large HTML response now)
  {:http-xhrio
   {:method :delete
    :uri src
    :body (doto (js/FormData.)
            (.append "attachment_link" "true")
            (.append "entity_type" (:entity_type attachment))
            (.append "entity_id" (:entity_id attachment)))
    :response-format (ajax/raw-response-format)
    :on-success [::deleted entity id]
    :on-failure [::events/api-error [::delete entity id] {}]}})

(re-frame/reg-event-fx
 ::delete
 delete-attachment)

(re-frame/reg-event-fx
 ::deleted
 (fn [{:keys [db]} [_ entity id _result]]
   {:db (update-in db [:entity entity :attachments :data]
                   (fn [data]
                     (remove #(= id (:id %)) data)))
    :fx [[:dispatch [::events/fetch-history]]]}))

;; stage-delete
(re-frame/reg-event-fx
  ::stage-delete
  (fn [{:keys [db]} [_ entity {:keys [id]} _result]]
    {:db (update-in db [:entity entity :attachments :stage]
                    (fn [data]
                      (remove #(= id (:id %)) data)))}))

;; rename
(defn rename-attachment [_ [_ entity {:keys [id rename_uri] :as attachment} new-name]]
  {:http-xhrio
   {:method :post
    :uri rename_uri
    :params (merge {:new_name new-name}
                   (select-keys attachment [:entity_type :entity_id]))
    :format (ajax/json-request-format)
    :response-format (ajax/json-response-format {:keywords? true})
    :on-success [::renamed entity id]
    :on-failure [::events/api-error [::rename entity id] {}]}})

(re-frame/reg-event-fx
 ::rename
 rename-attachment)

(re-frame/reg-event-fx
 ::renamed
 (fn [{:keys [db]} [_ entity id result]]
   {:db (update-in db [:entity entity :attachments :data]
                   (fn [data]
                     (mapv #(if (= id (:id %)) (:data result) %) data)))
    :fx [[:dispatch [::events/fetch-history]]]}))

;; stage-rename
(re-frame/reg-event-fx
  ::stage-rename
  (fn [{:keys [db]} [_ entity id new-name]]
    {:db (update-in db [:entity entity :attachments :stage]
                    (fn [data]
                      (mapv #(if (= id (:id %)) (assoc % :name new-name) %) data)))}))

;; notifications
(re-frame/reg-event-db
 ::clear-notifications
 (fn [db [_ entity]]
   (assoc-in db [:entity entity :attachments :notifications] {})))
;;

;;; utils

(defn image? [mime-type]
  (string/starts-with? mime-type "image/"))

(def spreadsheet-mime-types
  #{"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    "application/vnd.ms-excel"})

(def document-mime-types
  #{"application/msword"
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"})

(defn attachment-type [{:keys [type]}]
  (cond
    (image? type) :image
    (spreadsheet-mime-types type) :spreadsheet
    (document-mime-types type) :document
    :else :generic))

;;; components

(defn menu-item [{:keys [id label on-click icon]}]
  [:a.menu-item-element
   {:id id :on-click on-click}
   [:span.icon-holder [icon]]
   [overflow/overflow-with-mux-tooltip {}
    (fn [props]
      [:div.menu-item-text props label])]])

(defn paste-listener [{:keys [on-paste]}]
  (reagent/create-class
   {:component-did-mount (fn [_]
                           (.addEventListener (.querySelector js/document "body")
                                              "paste"
                                              on-paste))
    :component-will-unmount (fn [_]
                              (.removeEventListener (.querySelector js/document "body")
                                                    "paste"
                                                    on-paste))
    :reagent-render (fn [_]
                      [:span])}))

(defn drop-area-card [{:keys [on-upload]}]
  (let [state (reagent/atom {})
        file-input-ref (atom nil)
        on-drag-over (fn [e]
                       ;; Change mouse pointer
                       (set! (.-dropEffect (.-dataTransfer e)) "copy")
                       ;; Required for on-drop to fire
                       (dom/prevent-default e)
                       (when-not (:dragged-over? @state)
                         (swap! state assoc :dragged-over? true)))
        on-drag-leave (fn [_]
                        (swap! state assoc :dragged-over? false))
        process-files (fn [source]
                        (doseq [file (seq (.-files source))]
                          (on-upload file)))
        on-drop (fn [e]
                  (swap! state assoc :dragged-over? false)

                  ;; Prevent default handling
                  (dom/prevent-default e)
                  (dom/stop-propagation e)

                  (process-files (.-dataTransfer e)))
        on-paste (fn [e]
                   ;; Prevent default handling
                   (dom/prevent-default e)
                   (dom/stop-propagation e)

                   (process-files (.-clipboardData e)))
        on-select-files (fn [e]
                          (process-files (.-target e)))]
    (fn [_]
      [:div.attachment-card.attachment-drop-zone
       {:on-drag-over on-drag-over
        :on-drag-leave on-drag-leave
        :on-drop on-drop
        :class (when (:dragged-over? @state)
                 "active")}
       [paste-listener {:on-paste on-paste}]
       [:div.attachment-drop-zone-title
        [:input.visually-hidden
         {:ref #(reset! file-input-ref %)
          :multiple true
          :on-change on-select-files
          :type "file"}]
        [:p
         [icons/mux-download]
         ;; TODO: we need a better way to localize this kind of things (strings that contain components or links)
         [:span (get-translation "attachments.drop-zone.drag-n-drop" " Drag & Drop")]
         [:span " / "]
         [:span (get-translation "attachments.drop-zone.paste" "paste")]
         [:span " / "]
         [:span.ajax_attachments {:on-click (fn [e]
                                              (when-let [node @file-input-ref]
                                                (.click node))
                                              (dom/prevent-default e))}
          (get-translation "attachments.drop-zone.browse" "browse")]
         [:span (get-translation "attachments.drop-zone.files" " files")]]]
       [:div.attachment-drop-zone-subtitle
        [:p (get-translation "attachments.size-limit" "Up to 50MB per file")]]])))

(defn confirm-delete [{:keys [show? hide! on-delete attachment]}]
  [confirmation/confirm
   {:show? show?
    :title (get-translation "attachments.delete-confirmation.title" "Delete Attachment?")
    :body (format-translation
           (get-translation "attachments.delete-confirmation.body"
                            "\"%{attachment-name}\" will be deleted permanently.")
           :attachment-name (fu/truncate-text (:name @attachment) 30))
    :yes-label (get-translation "attachments.delete-confirmation.yes" "Delete")
    :on-yes (fn [_]
              (hide!)
              (on-delete @attachment))
    :no-label (get-translation "attachments.delete-confirmation.no" "Cancel")
    :on-no (fn [_]
             (hide!))}])

(defn confirm-cancel-uploads [{:keys [show? hide! on-cancel-uploads]}]
  [confirmation/confirm
   {:show? show?
    :title (get-translation "attachments.confirm-cancel-uploads.title" "Cancel the upload?")
    :body (get-translation "attachments.confirm-cancel-uploads.body" "The upload is imcomplete")
    :yes-label (get-translation "attachments.confirm-cancel-uploads.yes" "Cancel upload")
    :on-yes (fn [_]
              (hide!)
              (on-cancel-uploads))
    :no-label (get-translation "attachments.confirm-cancel-uploads.no" "Dismiss")
    :on-no (fn [_]
             (hide!))}])

(defn copy-attachment-link
  "This function copies the markdown link of an attachment to the clipboard.

  Parameters:
  attachment - A map representing the attachment. It should contain keys for :url, :name, and :type. 
               The :type key should be a MIME type that indicates the type of the attachment.

  The function generates a markdown link for the attachment. If the MIME type indicates that the 
  attachment is an image, the link will be in the format ![name](url?style=thumb). For other types 
  of attachments, the link will be in the format [name](url). This link is then copied to the clipboard."
  [{:keys [url name type]}]
  (let [text (if (image? type)
               (str "![" name "](" (set-query-param url "style" "thumb") ")")
               (str "[" name "](" url ")"))]
    (dom/text->clipboard text)))

(defn dropdown-menu [state _ _ _]
  (let [hide!   #(swap! state assoc :show-menu? false)
        toggle! #(swap! state update :show-menu? not)
        confirm-delete! #(swap! state assoc :show-delete-confirmation? true)
        show-confirm-delete? (reagent/cursor state [:show-delete-confirmation?])
        hide-confirm-delete! #(swap! state assoc :show-delete-confirmation? false)]
    (fn [_ permissions {:keys [id] :as attachment} {:keys [on-preview on-delete]}]
      (let [show? (:show-menu? @state)]
        [:div.float-end
         [overlay2/floating-ui-overlay
          {:placement        "bottom-start"
           :container        (.-body js/document)
           :offset           5
           :on-request-close hide!
           :show?            show?}

          [:div.attachment-dropdown-trigger
           {:id       (str "attachment-dropdown-" id)
            :on-click toggle!}
           [icons/mux-three-dots]]

          [:div.menu-popup
           (when (:can-preview? permissions)
             [menu-item
              {:id       (str "attachment-dropdown-" id "-preview")
               :label    (get-translation "attachments.dropdown-menu.preview" "Preview")
               :on-click on-preview
               :icon     icons/mux-eye}])
           (when (:can-download? permissions)
             [:a.menu-item-element
              {:id (str "attachment-dropdown-" id "-download")
               :on-click hide!
               :href (:url attachment)
               :download (:name attachment)}
              [:span.icon-holder [icons/mux-download2]]
              [overflow/overflow-with-mux-tooltip {}
               (fn [props]
                 [:div.menu-item-text props
                  (get-translation "attachments.dropdown-menu.download" "Download")])]])
           (when (:can-rename? permissions)
             [menu-item
              {:id       (str "attachment-dropdown-" id "-rename")
               :label    (get-translation "attachments.dropdown-menu.rename" "Rename")
               :on-click (fn [_]
                           (swap! state assoc :editing-name? true)
                           (hide!))
               :icon     icons/mux-pencil-rename}])
           (when (:can-delete? permissions)
             [menu-item
              {:id       (str "attachment-dropdown-" id "-delete")
               :label    (get-translation "attachments.dropdown-menu.delete" "Delete")
               :on-click (fn [_]
                           (hide!)
                           (confirm-delete!))
               :icon     icons/mux-trash}])
           (when (:can-copy-link? permissions)
             [menu-item
              {:id       (str "attachment-dropdown-" id "-copy-link")
               :label    (get-translation "attachments.dropdown-menu.copy-link" "Copy Link for Description")
               :on-click (fn [_]
                           (copy-attachment-link attachment)
                           (hide!))
               :icon     icons/mux-copy}])]]
         [confirm-delete
          {:show? show-confirm-delete?
           :hide! hide-confirm-delete!
           :on-delete on-delete
           :attachment (reagent/track (constantly attachment))}]]))))

(defn editable-name [{:keys [can-edit? edit! editing? value set-value! save! cancel!]}]
  (let [validating-save! (fn [event]
                           (let [target (.-target event)]
                             (when (.checkValidity target)
                               (save!))))]
    [:div.editable-text-container
     (if @editing?
       [:input.editable-input-text
        {:type "text"
         :pattern ".{1,}"
         :required true
         :auto-focus true
         :value @value
         :on-change set-value!
         :on-blur validating-save!
         :on-key-down (fn [e]
                        (case (.-key e)
                          "Escape" (do
                                     (dom/stop-propagation e)
                                     (dom/prevent-default e)
                                     (cancel!))
                          "Enter" (do
                                    (dom/stop-propagation e)
                                    (dom/prevent-default e)
                                    (validating-save! e))
                          true))}]
       [:div.editable-text
        {:on-click #(when can-edit?
                      (edit!))
         :title @value}
        @value])]))

(defmulti thumbnail attachment-type)

(defmethod thumbnail :image [{:keys [url]}]
  [:div.attachment-thumbnail.image
   [:img {:src url}]])

(defmethod thumbnail :spreadsheet [_]
  [:div.attachment-thumbnail.spreadsheet
   [icons/mux-spreadsheet]])

(defmethod thumbnail :document [_]
  [:div.attachment-thumbnail.document
   [icons/mux-document]])

(defmethod thumbnail :default [_]
  [:div.attachment-thumbnail
   [icons/mux-generic-attachment]])

(defn author-info [{:keys [author created-at]}]
  [:div.attachment-author-info
   [tooltip/mux-tooltip
    {}
    ;; Uploaded By Kenny Lane at 24/06/22, 09:21 AM
    [:div.attachment-uploaded-by
     [:span (format-translation
             (get-translation "attachments.uploaded-by"
                              "Uploaded By %{user-name}")
             :user-name (or (:name author) "unknown"))]
     [:span (format-translation
             (get-translation "attachments.uploaded-at"
                              "at %{date-time}")
             :date-time (fu/formatted-date-time created-at))]]
    (fn [props]
      [:span props [icons/mux-clock]])]])

(defn size-info [{:keys [size]}]
  [:p.attachment-size
   [:span.size-title "Size: "]
   (fu/format-file-size size)])

(defn card [permissions _ {:keys [on-rename on-preview] :as actions}]
  (let [state (reagent/atom {:editing-name? false})]
    (fn [_ attachment _]
      (let [name-container (reagent/atom {:name (:name attachment)})]
        [:div.attachment-card.attachment-item
         [:div
          {:on-click #(on-preview)}
          [thumbnail attachment]]
         [:div.attachment-meta
          [:div.attachment-info
           [editable-name
            {:can-edit? (:can-rename? permissions)
             :editing? (reagent/cursor state [:editing-name?])
             :edit! #(swap! state assoc :editing-name? true)
             :value (reagent/cursor name-container [:name])
             :set-value! #(swap! name-container assoc :name (dom/event-value %))
             :save! (fn []
                      (when (not= (:name attachment) (:name @name-container))
                        (on-rename attachment (:name @name-container)))
                      (swap! state assoc :editing-name? false))
             :cancel! (fn []
                        (swap! name-container assoc :name (:name attachment))
                        (swap! state assoc :editing-name? false))}]
           [author-info attachment]]
          [size-info attachment]]
         [dropdown-menu state permissions attachment actions]]))))

(defn notification-area [notifications {:keys [on-clear-notifications on-cancel-uploads]}]
  (let [{:keys [uploading uploaded failed]} @notifications]
    (when (or (pos? uploading) (pos? uploaded) (pos? failed))
      (let [icon (cond
                   (pos? uploading) [icons/mux-spinner]
                   (pos? failed) [:div.text-error [icons/mux-notification-error]]
                   :else [:div.text-success [icons/mux-notification-success]])
            ;; NOTE: not sure it's a good idea to join and capitalize localized strings
            status (->> [(when (pos? uploading)
                           (format-translation
                            (get-translation "attachments.notifications.uploading"
                                             "uploading %{n} files")
                            :n uploading))
                         (when (pos? uploaded)
                           (format-translation
                            (get-translation "attachments.notifications.uploaded"
                                             "uploaded %{n} files")
                            :n uploaded))
                         (when (pos? failed)
                           (format-translation
                            (get-translation "attachments.notifications.failed"
                                             "%{n} failed")
                            :n failed))]
                        (remove nil?)
                        (string/join ", ")
                        (string/capitalize))
            button (if (pos? uploading)
                     [:a.attachments-notifications-discard
                      {:href "#"
                       :on-click (fn [e]
                                   (dom/stop-propagation e)
                                   (dom/prevent-default e)
                                   (on-cancel-uploads))}
                      (get-translation "attachments.notifications.cancel" "Cancel")]
                     [:a.attachments-notifications-discard
                      {:href "#"
                       :on-click (fn [e]
                                   (dom/stop-propagation e)
                                   (dom/prevent-default e)
                                   (on-clear-notifications))}
                      (get-translation "attachments.notifications.close"
                                       "Close")])]

        [:div.attachments-notifications-container
         [:div.attachments-notifications
          [:div.attachments-notifications-icon icon]
          [:div.attachments-notifications-status status]
          button]]))))

(defn preview-modal-header [{:keys [previous! next! current-title]}]
  [:div.attachments-preview-title
   [:span (get-translation "attachments.preview.title" "Preview Attachment")]
   [:span.navigation-button {:on-click previous!} [icons/mux-chevron-left]]
   [:span @current-title]
   [:span.navigation-button {:on-click next!} [icons/mux-chevron-right]]])

(defn preview-editable-name [{:keys [current-attachment on-rename] :as actions}]
  (let [state (reagent/atom {:editing? false
                             :name (:name @current-attachment)})]
    [editable-name
     (assoc actions
            :can-edit? (boolean on-rename)
            :edit! #(swap! state assoc :editing? true)
            :save! (fn []
                     (when (not= (:name @current-attachment) (:name @state))
                       (on-rename @current-attachment (:name @state)))
                     (swap! state assoc :editing? false))
            :cancel! (fn []
                       (swap! state assoc
                              :name (:name @current-attachment)
                              :editing? false))
            :editing? (reagent/cursor state [:editing?])
            :value (reagent/cursor state [:name])
            :set-value! #(swap! state assoc :name (dom/event-value %)))]))

(defn not-previewable-attachment [{:keys [url name]}]
  [:<>
   [:p (get-translation "attachments.preview.cannot-preview" "The file cannot be previewed.")]
   ;; TODO: figure out how to properly localize this kind of things
   ;;       one option can be to translate "try to ", "download", and " it" separately
   ;;       another is to have a placeholder that will be replaced with raw HTML
   ;;       don't like either of the options so far
   [:p "You can " [:a {:href url :download name} "download"] " it"]])

(defmulti preview-content attachment-type)

(defmethod preview-content :image [{:keys [url]}]
  [:div.attachments-preview-content
   [:img {:src url}]])

(defmethod preview-content :spreadsheet [attachment]
  [:div.attachments-preview-content.spreadsheet
   [icons/mux-spreadsheet]
   [not-previewable-attachment attachment]])

(defmethod preview-content :document [attachment]
  [:div.attachments-preview-content.document
   [icons/mux-document]
   [not-previewable-attachment attachment]])

(defmethod preview-content :default [attachment]
  [:div.attachments-preview-content
   [icons/mux-generic-attachment]
   [not-previewable-attachment attachment]])

(defn preview-modal-body [{:keys [show-delete-confirmation!
                                  current-attachment previous! next!] :as actions}]
  [:div.attachments-preview-body
   [:div.attachments-preview-toolbar
    [:div.attachments-preview-toolbar-name
     [preview-editable-name actions]]
    [:div.attachments-preview-toolbar-buttons.m-button-group
     (when show-delete-confirmation!
       [buttons/button
        {:on-click show-delete-confirmation!
         :icon [icons/mux-trash]
         :text (get-translation "attachments.dropdown-menu.delete" "Delete")}])
     [buttons/button
      {:href (:url @current-attachment)
       :download (:name @current-attachment)
       :icon [icons/mux-download2]
       :text (get-translation "attachments.download-menu.download" "Download")}]]]
   [preview-content @current-attachment]
   [:span.attachments-preview-navigation.attachments-preview-previous
    {:on-click previous!}
    [icons/mux-chevron-left]]
   [:span.attachments-preview-navigation.attachments-preview-next
    {:on-click next!}
    [icons/mux-chevron-right]]])

(defn preview-modal [{:keys [show? hide!
                             show-delete-confirmation? hide-delete-confirmation!
                             on-delete current-attachment] :as actions}]
  [:<>
   [modal/bootstrap-modal
    {:class "attachments-preview-modal"
     :is-open? @show?
     :on-request-close hide!
     :close-button? true
     :centered? true}
    [preview-modal-header actions]
    [preview-modal-body actions]
    nil]
   (when on-delete
     [confirm-delete
      {:show? show-delete-confirmation?
       :hide! hide-delete-confirmation!
       :on-delete on-delete
       :attachment current-attachment}])])

(defn toolbar [{:keys [placeholder state filtered-count total-count]}]
  [:div.attachments-toolbar
   [search-box/search-textbox
    {:placeholder placeholder
     :value (:search-string @state)
     :on-change #(swap! state assoc :search-string (dom/event-value %))
     :on-clear-click #(swap! state assoc :search-string "")}]
   [:div.attachments-counter
    (format-translation
     (get-translation "attachments.counter" "%{x} of %{y}")
     :x filtered-count
     :y total-count)]])

(defn cards [{:keys [state permissions actions attachments]}]
  [:div.attachment-cards
   (when (:can-upload? permissions)
     [drop-area-card actions])
   (if (and (not (string/blank? (:search-string @state)))
            (empty? attachments))
     [:div.no-attachments-found
      [icons/mux-nothing-found]
      [:p "No Attachments were found"]]
     (for [[i {:keys [id] :as attachment}] attachments]
       ^{:key (str "att-" id)}
       [card permissions attachment
        (merge actions
               {:on-preview (fn []
                              (swap! state assoc
                                     :current-attachment-idx i
                                     :show-preview? true))})]))])

(defn notifications-view [{:keys [state notifications actions]}]
  (let [show-cancel-uploads? (reagent/cursor state [:show-cancel-uploads?])
        show-cancel-uploads! #(swap! state assoc :show-cancel-uploads? true)
        hide-cancel-uploads! #(swap! state assoc :show-cancel-uploads? false)
        on-cancel-uploads (:on-cancel-uploads actions)]
    [:<>
     [confirm-cancel-uploads
      {:show? show-cancel-uploads?
       :hide! hide-cancel-uploads!
       :on-cancel-uploads on-cancel-uploads}]
     [notification-area notifications
      (assoc actions :on-cancel-uploads show-cancel-uploads!)]]))

(defn preview [{:keys [state indexed-attachments actions]}]
  (let [hide-preview! #(swap! state assoc :show-preview? false)
        previous-preview! (fn []
                            (swap! state update :current-attachment-idx
                                   (fn [current-attachment-idx]
                                     (if (zero? current-attachment-idx)
                                       (dec (count indexed-attachments))
                                       (dec current-attachment-idx)))))
        next-preview! (fn []
                        (swap! state update :current-attachment-idx
                               (fn [current-attachment-idx]
                                 (if (= (dec (count indexed-attachments))
                                        current-attachment-idx)
                                   0
                                   (inc current-attachment-idx)))))
        current-attachment (reagent/track
                            (fn []
                              (when-let [idx (:current-attachment-idx @state)]
                                (last (first (drop idx indexed-attachments))))))
        current-title (reagent/track
                       (fn []
                         (str (inc (:current-attachment-idx @state))
                              "/"
                              (count indexed-attachments))))
        show-delete-confirmation? (reagent/cursor state [:show-preview-delete-confirmation?])
        show-delete-confirmation! #(swap! state assoc :show-preview-delete-confirmation? true)
        hide-delete-confirmation! #(swap! state assoc :show-preview-delete-confirmation? false)]
    [preview-modal
     (-> actions
         (assoc :show? (reagent/cursor state [:show-preview?])
                :hide! hide-preview!
                :previous! previous-preview!
                :next! next-preview!
                :current-title  current-title
                :current-attachment current-attachment
                :show-delete-confirmation? show-delete-confirmation?
                :show-delete-confirmation! (when (:on-delete actions) show-delete-confirmation!)
                :hide-delete-confirmation! hide-delete-confirmation!)
         (update :on-delete
                 (fn [on-delete]
                   (when on-delete
                     (fn []
                       (hide-preview!)
                       (on-delete @current-attachment))))))]))

(defmulti attachments-view
  (fn [{:keys [options]}]
    (:layout @options)))

(defmethod attachments-view "cards" [_]
  (let [state (reagent/atom {:search-string ""
                             :show-cancel-uploads? false})]
    (fn [{:keys [options attachments actions notifications]}]
      (let [indexed-attachments (vec (map-indexed vector @attachments))
            filtered-attachments (if (string/blank? (:search-string @state))
                                   indexed-attachments
                                   (vec (filter (fn [[_ {:keys [name]}]]
                                                  (fu/substring-search? name (:search-string @state)))
                                                indexed-attachments)))]
        [:<>
         [:div.attachments-container
          [toolbar {:state state
                    :filtered-count (count filtered-attachments)
                    :total-count (count indexed-attachments)
                    :placeholder (get-translation "entity.attachment.search-placeholder" "Search Attachment")}]
          [cards {:state state
                  :permissions (:permissions @options)
                  :actions actions
                  :attachments filtered-attachments}]
          [notifications-view {:state state
                               :notifications notifications
                               :actions actions}]
          [preview {:state state
                    :indexed-attachments indexed-attachments
                    :actions (cond-> actions
                               (not (get-in @options [:permissions :can-rename?]))
                               (dissoc :on-rename)

                               (not (get-in @options [:permissions :can-delete?]))
                               (dissoc :on-delete))}]]]))))

(defmethod attachments-view :default [{:keys [options]}]
  [:div
   [:p (str "Layout [" (:layout @options) "] is not implemented")]])

(comment

  (require 're-frame.db)

  (tap> (re-frame/subscribe [::attachments :testset]))

  @(re-frame/subscribe [::attachment-name :testset 153043])

  (re-frame/dispatch [:practitest.frontend.events/fetch-attachments])

  (tap> re-frame.db/app-db))
